import { NavLink, useLocation } from "react-router-dom";
import { useUnmatchedCertificateCount } from "../../features/unmatched-certificates/useUnmatchedCertificateCount";
import { useAuth } from "../../features/auth/useAuth";
import type { Role } from "../../lib/types";
import styles from "./SideNav.module.css";

type NavItem = {
  /** Internal route (react-router NavLink). Mutually exclusive with href. */
  to?: string;
  /** External/absolute-path URL, opened in a new tab. Mutually exclusive with to. */
  href?: string;
  label: string;
  icon: string; // inline SVG path data — no external icon library
  /** Roles allowed to see this item. Omitted = every authenticated role. */
  roles?: Role[];
  /** Item shows a waiting-count pill fed by this key. */
  countKey?: "unmatchedCertificates";
};

const NAV_ITEMS: NavItem[] = [
  {
    to: "/campaigns",
    label: "Campaigns",
    icon: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
  },
  {
    to: "/vendors",
    label: "Vendors",
    icon: "M3 21h18M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M9 21V10h4M9 9h.01M9 12h.01M9 15h.01M13 9h.01M13 12h.01M13 15h.01M19 21V9a2 2 0 00-2-2h-1",
  },
  {
    to: "/contracts",
    label: "Contracts",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    to: "/onboarders",
    label: "Onboarders",
    icon: "M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8zM22 21l-2-2m0 0a3 3 0 10-4.24-4.24A3 3 0 0020 19z",
    roles: ["Admin", "Staff"],
  },
  {
    to: "/resources",
    label: "Resources",
    icon: "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z",
    roles: ["Admin", "Staff"],
  },
  {
    to: "/exports",
    label: "Exports",
    icon: "M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2",
    roles: ["Admin", "Staff"],
  },
  {
    to: "/approval-queue",
    label: "Approval Queue",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
    roles: ["Admin", "Staff"],
  },
  {
    to: "/preparation-queue",
    label: "Preparation queue",
    icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
    roles: ["Admin", "Staff"],
  },
  {
    to: "/assistant",
    label: "Help assistant",
    icon: "M8 10h8M8 14h5M21 12a8 8 0 01-11.6 7.1L4 20l1-4.4A8 8 0 1121 12z",
    roles: ["Admin", "Staff"],
  },
  {
    to: "/users",
    label: "Users",
    icon: "M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
    roles: ["Admin"],
  },
  {
    to: "/api-keys",
    label: "API keys",
    icon: "M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4",
    roles: ["Admin"],
  },
  {
    to: "/unmatched-certificates",
    label: "Unmatched certificates",
    icon: "M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z",
    roles: ["Admin"],
    countKey: "unmatchedCertificates",
  },
  {
    to: "/settings/email",
    label: "Email settings",
    icon: "M4 4h16v16H4V4zM22 6l-10 7L2 6",
    roles: ["Admin"],
  },
  {
    href: "/api/v1/docs",
    label: "API docs",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
];

function NavIcon({ path }: { path: string }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  );
}

/** SideNav — primary tab navigation. Desktop: fixed column. Mobile: off-canvas drawer (driven by AppShell). */
export default function SideNav() {
  const userRoles = useAuth().user?.roles ?? [];
  const isAdmin = userRoles.includes("Admin");
  const pending = useUnmatchedCertificateCount(isAdmin, useLocation().pathname);
  const items = NAV_ITEMS.filter(
    (i) => !i.roles || i.roles.some((r) => userRoles.includes(r)),
  );

  return (
    <aside className={styles.sidebar} aria-label="Main navigation">
      <nav aria-label="Primary navigation">
        <ul role="list" className={styles.navList}>
          {items.map(({ to, href, label, icon, countKey }) => {
            const count =
              countKey === "unmatchedCertificates" && pending ? pending : 0;
            return (
              <li key={to ?? href}>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.navLink}
                  >
                    <NavIcon path={icon} />
                    <span className={styles.navLabel}>{label}</span>
                  </a>
                ) : (
                  <NavLink
                    to={to as string}
                    aria-label={
                      count > 0 ? `${label}, ${count} waiting` : undefined
                    }
                    className={({ isActive }) =>
                      [styles.navLink, isActive ? styles.navLinkActive : ""]
                        .filter(Boolean)
                        .join(" ")
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <NavIcon path={icon} />
                        <span className={styles.navLabel}>{label}</span>
                        {count > 0 && (
                          <span className={styles.countPill} aria-hidden="true">
                            {count}
                          </span>
                        )}
                        {isActive && (
                          <span className="sr-only">(current page)</span>
                        )}
                      </>
                    )}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
